<template>
	<div class="banners__example">
		<div class="banners__example-header">
			<UIHeading level="5" class="banners__example-header-title">
				{{ $t('pages.publishers.sections.formats.bannersContent.example') }}
			</UIHeading>
			<span class="banners__example-selected">
				{{ selectedFormat }}
			</span>
		</div>
		<div class="banners__example-media">
			<img
				src="/images/pages/publishers/formats/Macbook.png"
				alt="Banner Example"
				loading="lazy"
				decoding="async"
				class="banners__example-media-img" />

			<div ref="contentRef" class="banners__example-content">
				<div class="banners__example-container" :class="{ 'is-horizontal': isHorizontal }">
					<div ref="bannerRef" class="banners__example-banner">
						<img
							src="/images/pages/publishers/intro-bg.png"
							alt=""
							aria-hidden="true"
							loading="lazy"
							decoding="async"
							class="banners__example-banner-bg" />
						<img
							src="/images/pages/publishers/formats/banner-veon-logo.svg"
							alt="Veon logo"
							class="banners__example-banner-logo" />
					</div>
					<TransitionGroup tag="div" name="skeleton" class="banners__example-skeletons">
						<div v-for="i in skeletonCount" :key="i" class="banners__example-skeletons-item" />
					</TransitionGroup>
				</div>
			</div>
		</div>
		<div class="banners__example-controls">
			<button
				type="button"
				class="banners__example-controls-btn"
				aria-label="Предыдущий размер"
				@click="selectPrevFormat">
				<Icon name="mdi:chevron-left" size="28" />
			</button>
			<button
				type="button"
				class="banners__example-controls-btn"
				aria-label="Следующий размер"
				@click="selectNextFormat">
				<Icon name="mdi:chevron-right" size="28" />
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
	const props = defineProps<{
		selectedFormat: string
		formats: string[]
	}>()
	const emit = defineEmits<{
		(event: 'change-format', value: string): void
	}>()

	const { $gsap: gsap } = useNuxtApp()
	const bannerRef = ref<HTMLElement | null>(null)
	const contentRef = ref<HTMLElement | null>(null)
	const skeletonCount = ref(8)
	const isHorizontal = ref(false)

	// Reference banners: largest in each group — all formats in the group are
	// scaled by the same factor so relative sizes are preserved across selections.
	const REF_HORIZONTAL = { w: 960, h: 150 }
	const REF_VERTICAL = { w: 300, h: 500 }
	let resizeObserver: ResizeObserver | null = null

	const parseFormat = (format: string) => {
		// Support both multiplication symbol and latin "x": "300×250" and "300x250".
		const [rawW = '', rawH = ''] = format.split(/[×xX]/)
		const w = Number(rawW.trim())
		const h = Number(rawH.trim())
		return { w, h }
	}

	const updateOrientation = (format: string) => {
		const { w, h } = parseFormat(format)
		if (!w || !h) return

		isHorizontal.value = w > h
		skeletonCount.value = isHorizontal.value ? 5 : 8
	}

	const computeGroupScale = (horizontal: boolean): number => {
		if (!contentRef.value) return 1
		const containerW = contentRef.value.offsetWidth
		const containerH = contentRef.value.offsetHeight

		if (horizontal) {
			return Math.min(containerW / REF_HORIZONTAL.w, (containerH * 0.38) / REF_HORIZONTAL.h)
		}
		return Math.min((containerW * 0.38) / REF_VERTICAL.w, (containerH * 0.72) / REF_VERTICAL.h)
	}

	const applyBannerSize = (format: string, animate = true) => {
		const { w, h } = parseFormat(format)
		if (!bannerRef.value || !contentRef.value || !w || !h) return

		const horizontal = w > h
		const scale = computeGroupScale(horizontal)
		const containerW = contentRef.value.offsetWidth
		const containerH = contentRef.value.offsetHeight

		const wPct = `${((w * scale) / containerW) * 100}%`
		const hPct = `${((h * scale) / containerH) * 100}%`

		if (animate) {
			gsap.to(bannerRef.value, { width: wPct, height: hPct, duration: 0.5, ease: 'power3.out' })
		} else {
			gsap.set(bannerRef.value, { width: wPct, height: hPct })
		}
	}

	const selectPrevFormat = () => {
		if (!props.formats.length) return
		const currentIndex = props.formats.indexOf(props.selectedFormat)
		const prevIndex = currentIndex <= 0 ? props.formats.length - 1 : currentIndex - 1
		const prevFormat = props.formats[prevIndex]
		if (prevFormat) emit('change-format', prevFormat)
	}

	const selectNextFormat = () => {
		if (!props.formats.length) return
		const currentIndex = props.formats.indexOf(props.selectedFormat)
		const nextIndex =
			currentIndex === -1 || currentIndex >= props.formats.length - 1 ? 0 : currentIndex + 1
		const nextFormat = props.formats[nextIndex]
		if (nextFormat) emit('change-format', nextFormat)
	}

	watch(
		() => props.selectedFormat,
		(format) => {
			updateOrientation(format)
			applyBannerSize(format)
		}
	)

	onMounted(async () => {
		await nextTick()
		updateOrientation(props.selectedFormat)
		applyBannerSize(props.selectedFormat, false)

		resizeObserver = new ResizeObserver(() => applyBannerSize(props.selectedFormat, false))
		if (contentRef.value) resizeObserver.observe(contentRef.value)
	})

	onUnmounted(() => {
		resizeObserver?.disconnect()
		resizeObserver = null
	})
</script>

<style lang="scss" scoped>
	.banners__example {
		// flex: 0 0 50%;
		// max-width: 50%;
		position: sticky;
		top: 16px;
		width: 100%;

		@media (width <= 767.98px) {
			position: static;
		}
		&-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
		}
		&-selected {
			padding: 8px 16px;
			background-color: var(--primary);
			border-radius: 40px;
		}
		&-media {
			position: relative;
			display: flex;
			align-items: flex-start;
			margin-top: 32px;
			&-img {
				width: 100%;
				height: 100%;
			}
		}
		&-controls {
			display: flex;
			gap: 12px;
			justify-content: center;
			margin-top: 12px;
			&-btn {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: 44px;
				height: 44px;

				// border: 1px solid var(--gray400);
				background-color: var(--primary);
				border-radius: 50px;
				transition:
					background-color 0.2s ease,
					border-color 0.2s ease;
				&:hover {
					background-color: var(--primary-hover);
				}
			}
		}
		&-content {
			position: absolute;
			bottom: 10.5%;
			left: 7%;
			z-index: 1;
			// display: flex;
			width: 86%;
			height: 76.5%;
			overflow: auto;
			border-radius: 0 0 1.2% 1.2%;
		}
		&-container {
			display: flex;
			width: 100%;
			height: 100%;
			padding: 2%;
			gap: 3%;

			&.is-horizontal {
				flex-direction: column-reverse;
				gap: 4%;
				align-items: center;
			}
		}
		&-skeletons {
			display: grid;
			flex: 1;
			grid-template-columns: repeat(2, 1fr);
			grid-auto-rows: 1fr;
			gap: clamp(6px, 5%, 24px);
			min-width: 0;
			height: 100%;
			.is-horizontal & {
				flex: 1;
				gap: clamp(8px, 5%, 8px);
				width: 100%;
				min-width: unset;
				height: auto;
			}
			&-item {
				background-color: var(--gray400);
				border-radius: 4px;
				&:nth-child(n + 3) {
					grid-column: span 2;
				}
			}
		}
		:deep(.skeleton-enter-active),
		:deep(.skeleton-leave-active) {
			transition:
				opacity 0.3s ease,
				transform 0.3s ease;
		}
		:deep(.skeleton-move) {
			transition: transform 0.3s ease;
		}
		:deep(.skeleton-leave-active) {
			position: absolute;
		}
		:deep(.skeleton-enter-from),
		:deep(.skeleton-leave-to) {
			opacity: 0;
			transform: scale(0.85);
		}
		&-banner {
			position: relative;

			// border-radius: 6px;

			display: flex;
			flex-shrink: 0;
			align-items: center;
			justify-content: center;
			width: 30%;
			height: 80%;
			overflow: hidden;
			// position: absolute;
			// top: 50%;
			// left: 50%;
			// transform: translate(-50%, -50%);

			// box-shadow: 0 10px 30px rgb(0 0 0 / 10%);
			&-bg {
				position: absolute;
				inset: 0;
				z-index: 0;
				width: 100%;
				height: 100%;
				object-fit: cover;
			}
			&-logo {
				position: absolute;
				top: 50%;
				left: 10%;
				z-index: 1;
				width: 50%;
				max-width: 100px;
				height: 75%;
				object-fit: contain;
				transform: translateY(-50%);
			}
		}
	}
</style>
